import React from 'react';

import { Box, Paper, Typography } from '@mui/material';

import { Field } from '@hrlogics/react-components/form';
import { composeSx } from '@hrlogics/react-helper-utils';

import { CompanyType } from 'src/api';
import { NumberField, SwitchField, ToggleButtonGroupField } from 'src/components/finalForm';
import { REVENUE_SHARE_OPTIONS } from 'src/constants/billing';
import { COMPANY_TYPE_LABEL } from 'src/constants/company';
import { useCurrentUserPermissions } from 'src/hooks/user';
import { SxProp } from 'src/types/mui';

import { RevenueShareFormField } from '../constants';

type RevenueShareSettingsFormSectionProps = {
  companyType: CompanyType;
};

export const RevenueShareSettingsFormSection: React.FC<RevenueShareSettingsFormSectionProps> = ({
  companyType,
}) => {
  const { isPlatformAdmin } = useCurrentUserPermissions();

  return (
    <React.Fragment>
      <Paper sx={containerStyles} elevation={0}>
        <Typography component="h2" variant="subtitle1">
          Revenue Share
        </Typography>
        <Typography component="p" variant="body1" color="text.secondary">
          Is a percent or flat amount that {COMPANY_TYPE_LABEL[companyType]} gets from any request
        </Typography>

        <Box display="flex" padding="32px 0 0">
          <Field
            fullWidth
            label="Revenue Share"
            name={RevenueShareFormField.revenueShareValue}
            component={NumberField}
            placeholder="0"
            disabled={!isPlatformAdmin}
          />
          <Box marginLeft="16px">
            <Field
              name={RevenueShareFormField.revenueShareType}
              component={ToggleButtonGroupField}
              options={REVENUE_SHARE_OPTIONS}
              fullWidth
              exclusive
              disabled={!isPlatformAdmin}
              ToggleButtonProps={{ sx: { width: '56px', height: '56px' } }}
            />
          </Box>
        </Box>
      </Paper>

      {isPlatformAdmin ? (
        <Paper sx={composeSx(containerStyles, { display: 'flex', gap: 1 })} elevation={0}>
          <Box mr="auto">
            <Box display="flex" alignItems="center">
              <Typography variant="subtitle1" mr={3}>
                Revenue Share report
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Enable the toggle if that {COMPANY_TYPE_LABEL[companyType]} should be able to generate
              detailed Revenue Share reports
            </Typography>
          </Box>
          <Field
            sx={{ marginRight: 0 }}
            component={SwitchField}
            disabled={!isPlatformAdmin}
            name={RevenueShareFormField.revenueShareReportEnabled}
          />
        </Paper>
      ) : null}
    </React.Fragment>
  );
};

const containerStyles: SxProp = theme => ({
  padding: '24px',
  border: theme.constantStyles?.paperBorder,
  borderRadius: theme.constantStyles?.paperBorderRadius,
});
